import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { defer, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { GlobalLogoutService } from '../service/global-logout.service';
import { PASSIVE_REQUEST_HEADERS, SESSION_HEARTBEAT_PATH, SESSION_POLICY_PATH, SessionActivityService } from '../service/session-activity.service';
import { SessionPolicyService } from '../service/session-policy.service';
import { UserService } from '../service/user.service';

/** Response header the API sets when it ends or refuses a server-side session. */
export const SESSION_STATUS_HEADER = 'X-Session-Status';

/**
 * `X-Session-Status` values (on an HTTP 401) meaning the server-side session is over.
 * `store-unavailable` (HTTP 503) is a transient server fault and is deliberately absent.
 */
export const SESSION_ENDED_STATUSES: ReadonlySet<string> = new Set(['ended', 'idle-expired', 'absolute-expired', 'unknown']);

/** Re-exported from the activity service (kept here for existing importers). */
export { PASSIVE_REQUEST_HEADERS };

/** Lower-case name of the passive header, for the request check. */
const PASSIVE_HEADER_NAME = Object.keys(PASSIVE_REQUEST_HEADERS)[0];

/**
 * URL prefixes of the app's own API (v1 and v2). Only requests under these count as server activity;
 * an unset base is dropped, so a misconfigured environment counts nothing. Overridable in tests.
 */
export const SESSION_API_BASE_URLS = new InjectionToken<readonly string[]>('SESSION_API_BASE_URLS', {
  providedIn: 'root',
  factory: () => [environment.API_BASE_URL, environment.API_BASE_URL_V2].filter((b): b is string => typeof b === 'string' && b.length > 0),
});

/** Path suffix of the call that mints (and, on a refresh, continues) a session token. */
const TOKEN_GENERATE_SUFFIX = '/token/token-generate';

/** Path suffix of the logout revocation call, whose own failure must never start another logout. */
const TOKEN_EXPIRE_SUFFIX = '/token/token-expire-async';

/**
 * One-shot latch so a burst of concurrent 401s runs the global logout once. Re-armed when a
 * token-generate call succeeds, i.e. when a new login in the same page starts a new session.
 */
@Injectable({ providedIn: 'root' })
export class SessionEndLatch {
  private tripped = false;

  /** @returns `true` for the first caller since the last {@link reset}, `false` afterwards. */
  trip(): boolean {
    if (this.tripped) {
      return false;
    }
    this.tripped = true;
    return true;
  }

  /** Re-arms the latch for a new session. */
  reset(): void {
    this.tripped = false;
  }
}

/** Monotonic milliseconds for round-trip measurement (falls back to the wall clock where `performance` is unavailable). */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

/**
 * Extracts the pathname from an absolute or relative URL.
 * @param url - Request URL.
 * @returns The pathname, or the input when it cannot be parsed.
 */
function pathOf(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url;
  }
}

/**
 * N4 response header: the server states whether it counted the request as session activity.
 * `counted` | `not-counted`; ABSENT means not counted (Off mode, anonymous routes, requests rejected before the
 * activity filter, global 429, HMAC failures, a browser that cannot read the header because it is not CORS-exposed).
 * UNAPPROVED API CONTRACT. The server writes activity BEFORE the action runs, so `counted` can accompany a 4xx/5xx.
 */
export const ACTIVITY_HEADER = 'X-RT-Activity';

/** The only value of {@link ACTIVITY_HEADER} that advances the server-activity estimate. */
const ACTIVITY_COUNTED = 'counted';

/**
 * N5 rule. The server header, not a path prefix, decides whether a request counted. Only the paths whose
 * response must never be read as activity regardless of the header stay excluded, because they have their own sync:
 *  - `/token/token-generate`: mints/refreshes the token; the policy seed and session start are handled by UserService;
 *  - `/token/token-expire-async`: the logout revocation call;
 *  - `/token/session-heartbeat`: SessionIdleWarningService applies the heartbeat result (a failed heartbeat must
 *    not be double counted or extend anything);
 *  - `/token/session-policy` (D9): the passive startup / after-switch read. The server answers `not-counted`, and the
 *    client additionally never lets it touch the activity estimate (SessionPolicyReadService applies its own result).
 * Every other call (including /login/* and /token/* routes such as toggle-preference-visiblity,
 * insert-update-angular-choice and handoff-ticket) is judged by the request metadata below and the response header.
 * Anonymous calls carry no session bearer, so they cannot qualify.
 */

/**
 * Whether a request is a candidate for server-activity counting (decision D-10, unapproved; N4/N5): it must go
 * to the app's own API, carry an Authorization header, not be marked passive and not be one of the three
 * self-syncing paths. The remaining conditions (the bearer is the CURRENT session token when the response
 * arrives, and the response says `X-RT-Activity: counted`) are checked when the response lands.
 * @param url - Request URL.
 * @param path - Request pathname.
 * @param hasAuthorization - Request carries an Authorization header.
 * @param isPassive - Request carries the passive marker.
 * @param bases - Configured API base URLs.
 * @returns `true` when a response with the \`counted\` header may advance the server-activity estimate.
 */
function isCountedRequest(url: string, path: string, hasAuthorization: boolean, isPassive: boolean, bases: readonly string[]): boolean {
  return (
    hasAuthorization &&
    !isPassive &&
    bases.some((b) => url.startsWith(b)) &&
    !path.endsWith(TOKEN_GENERATE_SUFFIX) &&
    !path.endsWith(TOKEN_EXPIRE_SUFFIX) &&
    !path.endsWith(SESSION_HEARTBEAT_PATH) &&
    !path.endsWith(SESSION_POLICY_PATH)
  );
}

/**
 * @param headers - Response headers (an HttpResponse, or the headers of an HttpErrorResponse; absent for a network error).
 * @returns `true` only for `X-RT-Activity: counted` (case-insensitive name and value).
 */
function serverCounted(headers: HttpHeaders | null | undefined): boolean {
  return (headers?.get(ACTIVITY_HEADER) ?? '').trim().toLowerCase() === ACTIVITY_COUNTED;
}

/**
 * Runs the existing global logout when the server reports its session is over.
 *
 * Acts only on HTTP 401 carrying `X-Session-Status` in {@link SESSION_ENDED_STATUSES}. The
 * error is always rethrown untouched, so callers (including the token-refresh pipeline) still
 * see the failure and nothing here refreshes the token or retries the request. A header-less
 * 401 (wrong password, ordinary auth failure) and a 503 `store-unavailable` pass through
 * unchanged and never log the user out.
 *
 * The logout runs once per session: a {@link SessionEndLatch} absorbs concurrent 401s, the
 * logout's own `token-expire-async` revocation call is exempt, and nothing happens when no
 * token is stored any more (another logout path already tore the session down).
 * `GlobalLogoutService` and `UserService` are resolved lazily through the injector because
 * both depend on `HttpClient`, which would otherwise form a DI cycle with this interceptor.
 */
export const sessionStatusInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const latch = inject(SessionEndLatch);
  const path = pathOf(req.url);
  const bases = inject(SESSION_API_BASE_URLS);
  const countsAsActivity = isCountedRequest(req.url, path, req.headers.has('Authorization'), req.headers.has(PASSIVE_HEADER_NAME), bases);
  const activity = countsAsActivity ? inject(SessionActivityService) : null;
  const clock = bases.some((b) => req.url.startsWith(b)) ? inject(SessionPolicyService) : null;

  return defer(() => {
    const sentAtWall = Date.now();
    const sentAtMono = monotonicNow();
    /**
     * Reads a response (success OR error: HttpErrorResponse.headers carries the headers of a 4xx/5xx; a network
     * error, abort or missing response has none and therefore never counts).
     */
    const onResponseHeaders = (headers: HttpHeaders | null | undefined): void => {
      // R is captured when the response ARRIVES. The round trip is measured on the monotonic clock and S is derived as
      // R - roundTrip, so a wall-clock change during the request cannot make S and R inconsistent (contract C1-C5).
      const receivedAtMs = Date.now();
      const sentAtMs = receivedAtMs - Math.max(0, monotonicNow() - sentAtMono);
      // INERT (Phase 47B Q3): the sample is recorded for diagnostics only; no deadline is derived from the `Date` header (the absolute
      // deadline comes solely from a valid `server_time_utc` in the policy bodies).
      clock?.noteServerDate(headers?.get('Date') ?? null, Math.min(sentAtWall, receivedAtMs), receivedAtMs);
      // N4: only an explicit `counted` advances the estimate, whatever the HTTP status.
      if (activity && serverCounted(headers)) {
        // hasSessionToken(): a response landing after the session was torn down must not re-arm anything.
        // N5: and the request's bearer must be the CURRENT session token (anonymous / third-party / stale bearers do not count).
        const user = injector.get(UserService);
        if (user.hasSessionToken() && user.isCurrentSessionBearer(req.headers.get('Authorization'))) {
          // D5: the bound uses the server's REAL touch interval. Unknown or invalid (contract C6): nothing is derived.
          const touchMs = clock?.touchIntervalMs ?? null;
          if (touchMs !== null) {
            activity.recordRequest(sentAtMs, touchMs, receivedAtMs);
          }
        }
      }
    };
    return next(req).pipe(tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          onResponseHeaders(event.headers);
        }
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          onResponseHeaders(error.headers);
        }
      },
    }));
  }).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && path.endsWith(TOKEN_GENERATE_SUFFIX)) {
        latch.reset();
      }
    }),
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        SESSION_ENDED_STATUSES.has(error.headers?.get(SESSION_STATUS_HEADER) ?? '') &&
        !path.endsWith(TOKEN_EXPIRE_SUFFIX) &&
        injector.get(UserService).hasSessionToken() &&
        latch.trip()
      ) {
        injector.get(GlobalLogoutService).initiateLogout();
      }
      return throwError(() => error);
    })
  );
};
